import { createApi } from "@reduxjs/toolkit/query/react"
import { request, gql, ClientError } from "graphql-request"

const storefrontGraphqlApiUrl = `${
  import.meta.env.VITE_MYSHOPIFY_BASE_URL
}/api/${import.meta.env.VITE_SHOPIFY_STOREFRONT_API_VERSION}/graphql.json`

const requestHeaders = {
  "X-Shopify-Storefront-Access-Token": import.meta.env
    .VITE_SHOPIFY_STOREFRONT_PUBLIC_ACCESS_TOKEN,
  "X-GraphQL-Cost-Include-Fields": true,
}

const graphqlBaseQuery =
  ({ baseUrl }) =>
  async ({ body, variables = {} }) => {
    try {
      const result = await request(baseUrl, body, variables, requestHeaders)
      return { data: result }
    } catch (error) {
      if (error instanceof ClientError) {
        return { error: { status: error.response.status, data: error } }
      }
      return { error: { status: 500, data: error } }
    }
  }

export const MAILING_ADDRESS_FRAGMENT = gql`
  fragment MailingAddressFields on MailingAddress {
    company
    firstName
    lastName
    address1
    address2
    city
    province
    country
    zip
    phone
  }
`

export const CUSTOMER_FRAGMENT = gql`
  ${MAILING_ADDRESS_FRAGMENT}
  fragment CustomerFields on Customer {
    id
    displayName
    firstName
    lastName
    email
    acceptsMarketing
    addresses(first: 10) {
      edges {
        node {
          ...MailingAddressFields
        }
      }
    }
    defaultAddress {
      ...MailingAddressFields
    }
  }
`

export const LINE_ITEMS_FRAGMENT = gql`
  fragment LineItemsFields on Checkout {
    lineItems(first: 100) {
      edges {
        node {
          id
          title
          quantity
          unitPrice {
            amount
          }
          discountAllocations {
            allocatedAmount {
              amount
            }
            discountApplication {
              allocationMethod
              targetSelection
              targetType
              value {
                ... on MoneyV2 {
                  amount
                }
                ... on PricingPercentageValue {
                  percentage
                }
              }
              ... on DiscountCodeApplication {
                code
                applicable
              }
            }
          }
          variant {
            id
            title
            price {
              amount
            }
            product {
              id
              productType
              images(first: 1) {
                edges {
                  node {
                    url(transform: { maxHeight: 250 })
                  }
                }
              }
              metafield(namespace: "custom", key: "order_limits") {
                id
                namespace
                key
                value
              }
            }
          }
        }
      }
    }
  }
`

export const TOTAL_AMOUNT_FRAGMENT = gql`
  fragment TotalAmountFields on Checkout {
    lineItemsSubtotalPrice {
      amount
    }
    subtotalPrice {
      amount
    }
    totalTax {
      amount
    }
    totalPrice {
      amount
    }
    paymentDue {
      amount
    }
  }
`

const CHECKOUT_FRAGMENT = gql`
  ${MAILING_ADDRESS_FRAGMENT}
  ${LINE_ITEMS_FRAGMENT}
  ${TOTAL_AMOUNT_FRAGMENT}
  fragment CheckoutFields on Checkout {
    id
    completedAt
    ready
    email
    currencyCode
    ...LineItemsFields
    requiresShipping
    shippingAddress {
      ...MailingAddressFields
    }
    shippingLine {
      handle
      price {
        amount
      }
      title
    }
    appliedGiftCards {
      id
      amountUsed {
        amount
      }
      lastCharacters
    }
    discountApplications(first: 50) {
      edges {
        node {
          allocationMethod
          targetSelection
          targetType
          value {
            ... on MoneyV2 {
              amount
            }
            ... on PricingPercentageValue {
              percentage
            }
          }
          ... on DiscountCodeApplication {
            code
            applicable
          }
        }
      }
    }
    ...TotalAmountFields
    customAttributes {
      key
      value
    }
    note
    webUrl
  }
`

const storefrontApi = createApi({
  reducerPath: "storefrontApi",
  baseQuery: graphqlBaseQuery({
    baseUrl: storefrontGraphqlApiUrl,
  }),
  endpoints: (builder) => ({
    getCustomer: builder.query({
      query: (customerAccessToken) => ({
        body: gql`
          ${CUSTOMER_FRAGMENT}
          query getCustomer($customerAccessToken: String!) {
            customer(customerAccessToken: $customerAccessToken) {
              ...CustomerFields
            }
          }
        `,
        variables: {
          customerAccessToken,
        },
      }),
    }),
    getCheckout: builder.query({
      query: (checkoutId) => ({
        body: gql`
          ${CHECKOUT_FRAGMENT}
          query getCheckout($checkoutId: ID!) {
            node(id: $checkoutId) {
              ...CheckoutFields
            }
          }
        `,
        variables: {
          checkoutId,
        },
      }),
      transformResponse: (response) => ({ checkout: response.node }),
      providesTags: ["Checkout"],
    }),
    getCheckoutReady: builder.mutation({
      query: (checkoutId) => ({
        body: gql`
          query getCheckout($checkoutId: ID!) {
            node(id: $checkoutId) {
              ... on Checkout {
                id
                ready
                webUrl
              }
            }
          }
        `,
        variables: {
          checkoutId,
        },
        extensions: {
          cost: true,
        },
      }),
      transformResponse: (response) => ({ checkout: response.node }),
    }),
    createCheckout: builder.mutation({
      query: (checkoutCreateInput = {}) => ({
        body: gql`
          mutation checkoutCreate($input: CheckoutCreateInput!) {
            checkoutCreate(input: $input) {
              checkout {
                id
              }
              checkoutUserErrors {
                code
                field
                message
              }
            }
          }
        `,
        variables: {
          input: checkoutCreateInput,
        },
      }),
      invalidatesTags: ["Checkout"],
    }),
    replaceLineItems: builder.mutation({
      query: (params) => ({
        body: gql`
          ${LINE_ITEMS_FRAGMENT}
          ${TOTAL_AMOUNT_FRAGMENT}
          mutation checkoutLineItemsReplace(
            $lineItems: [CheckoutLineItemInput!]!
            $checkoutId: ID!
          ) {
            checkoutLineItemsReplace(
              lineItems: $lineItems
              checkoutId: $checkoutId
            ) {
              checkout {
                requiresShipping
                ...LineItemsFields
                ...TotalAmountFields
              }
              userErrors {
                code
                field
                message
              }
            }
          }
        `,
        variables: {
          checkoutId: params.checkoutId,
          lineItems: params.lineItems,
        },
      }),
    }),
    addLineItems: builder.mutation({
      query: (params) => ({
        body: gql`
          ${LINE_ITEMS_FRAGMENT}
          ${TOTAL_AMOUNT_FRAGMENT}
          mutation checkoutLineItemsAdd(
            $lineItems: [CheckoutLineItemInput!]!
            $checkoutId: ID!
          ) {
            checkoutLineItemsAdd(
              lineItems: $lineItems
              checkoutId: $checkoutId
            ) {
              checkout {
                ...LineItemsFields
                shippingLine {
                  handle
                  price {
                    amount
                  }
                  title
                }
                ...TotalAmountFields
              }
              checkoutUserErrors {
                code
                field
                message
              }
            }
          }
        `,
        variables: {
          checkoutId: params.checkoutId,
          lineItems: params.lineItems,
        },
      }),
    }),
    removeLineItems: builder.mutation({
      query: (params) => ({
        body: gql`
          ${LINE_ITEMS_FRAGMENT}
          ${TOTAL_AMOUNT_FRAGMENT}
          mutation checkoutLineItemsRemove(
            $checkoutId: ID!
            $lineItemIds: [ID!]!
          ) {
            checkoutLineItemsRemove(
              checkoutId: $checkoutId
              lineItemIds: $lineItemIds
            ) {
              checkout {
                ...LineItemsFields
                shippingLine {
                  handle
                  price {
                    amount
                  }
                  title
                }
                ...TotalAmountFields
              }
              checkoutUserErrors {
                code
                field
                message
              }
            }
          }
        `,
        variables: {
          checkoutId: params.checkoutId,
          lineItemIds: params.lineItemIds,
        },
      }),
    }),
    updateEmailAddress: builder.mutation({
      query: ({ checkoutId, email }) => ({
        body: gql`
          mutation checkoutEmailUpdateV2($checkoutId: ID!, $email: String!) {
            checkoutEmailUpdateV2(checkoutId: $checkoutId, email: $email) {
              checkout {
                id
                email
              }
              checkoutUserErrors {
                code
                field
                message
              }
            }
          }
        `,
        variables: {
          checkoutId,
          email,
        },
      }),
    }),
    updateShippingAddress: builder.mutation({
      query: ({ checkoutId, shippingAddress }) => ({
        body: gql`
          ${MAILING_ADDRESS_FRAGMENT}
          ${TOTAL_AMOUNT_FRAGMENT}
          mutation checkoutShippingAddressUpdateV2(
            $checkoutId: ID!
            $shippingAddress: MailingAddressInput!
          ) {
            checkoutShippingAddressUpdateV2(
              checkoutId: $checkoutId
              shippingAddress: $shippingAddress
            ) {
              checkout {
                id
                shippingAddress {
                  ...MailingAddressFields
                }
                shippingLine {
                  handle
                  price {
                    amount
                  }
                  title
                }
                ...TotalAmountFields
              }
              checkoutUserErrors {
                code
                field
                message
              }
            }
          }
        `,
        variables: {
          checkoutId,
          shippingAddress,
        },
      }),
    }),
    getAvailableShippingRates: builder.mutation({
      query: (params) => ({
        body: gql`
          query getAvailableShippingRates($checkoutId: ID!) {
            node(id: $checkoutId) {
              ... on Checkout {
                ready
                availableShippingRates {
                  ready
                  shippingRates {
                    handle
                    title
                    price {
                      amount
                    }
                  }
                }
              }
            }
          }
        `,
        variables: {
          checkoutId: params.checkoutId,
        },
      }),
    }),
    updateShippingLine: builder.mutation({
      query: ({ checkoutId, shippingRateHandle }) => ({
        body: gql`
          ${TOTAL_AMOUNT_FRAGMENT}
          mutation checkoutShippingLineUpdate(
            $checkoutId: ID!
            $shippingRateHandle: String!
          ) {
            checkoutShippingLineUpdate(
              checkoutId: $checkoutId
              shippingRateHandle: $shippingRateHandle
            ) {
              checkout {
                shippingLine {
                  handle
                  price {
                    amount
                  }
                  title
                }
                ...TotalAmountFields
              }
              checkoutUserErrors {
                code
                field
                message
              }
            }
          }
        `,
        variables: {
          checkoutId,
          shippingRateHandle,
        },
      }),
    }),
    applyDiscountCode: builder.mutation({
      query: (params) => ({
        body: gql`
          mutation checkoutDiscountCodeApplyV2(
            $checkoutId: ID!
            $discountCode: String!
          ) {
            checkoutDiscountCodeApplyV2(
              checkoutId: $checkoutId
              discountCode: $discountCode
            ) {
              checkoutUserErrors {
                code
                field
                message
              }
            }
          }
        `,
        variables: {
          checkoutId: params.checkoutId,
          discountCode: params.discountCode,
        },
      }),
      invalidatesTags: ["Checkout"],
    }),
    autoApplyDiscountCode: builder.mutation({
      query: (params) => ({
        body: gql`
          mutation checkoutDiscountCodeApplyV2(
            $checkoutId: ID!
            $discountCode: String!
          ) {
            checkoutDiscountCodeApplyV2(
              checkoutId: $checkoutId
              discountCode: $discountCode
            ) {
              checkoutUserErrors {
                code
                field
                message
              }
            }
          }
        `,
        variables: {
          checkoutId: params.checkoutId,
          discountCode: params.discountCode,
        },
      }),
    }),
    removeDiscountCode: builder.mutation({
      query: (params) => ({
        body: gql`
          mutation checkoutDiscountCodeRemove($checkoutId: ID!) {
            checkoutDiscountCodeRemove(checkoutId: $checkoutId) {
              checkoutUserErrors {
                code
                field
                message
              }
            }
          }
        `,
        variables: {
          checkoutId: params.checkoutId,
        },
      }),
      invalidatesTags: ["Checkout"],
    }),
    applyGiftCard: builder.mutation({
      query: (params) => ({
        body: gql`
          mutation checkoutGiftCardsAppend(
            $checkoutId: ID!
            $giftCardCodes: [String!]!
          ) {
            checkoutGiftCardsAppend(
              checkoutId: $checkoutId
              giftCardCodes: $giftCardCodes
            ) {
              checkoutUserErrors {
                code
                field
                message
              }
            }
          }
        `,
        variables: {
          checkoutId: params.checkoutId,
          giftCardCodes: [params.giftCardCode],
        },
      }),
      invalidatesTags: ["Checkout"],
    }),
    removeGiftCard: builder.mutation({
      query: (params) => ({
        body: gql`
          mutation checkoutGiftCardRemoveV2(
            $appliedGiftCardId: ID!
            $checkoutId: ID!
          ) {
            checkoutGiftCardRemoveV2(
              appliedGiftCardId: $appliedGiftCardId
              checkoutId: $checkoutId
            ) {
              checkoutUserErrors {
                code
                field
                message
              }
            }
          }
        `,
        variables: {
          checkoutId: params.checkoutId,
          appliedGiftCardId: params.appliedGiftCardId,
        },
      }),
      invalidatesTags: ["Checkout"],
    }),
    updateCheckoutAttributes: builder.mutation({
      query: ({ checkoutId, input }) => ({
        body: gql`
          mutation checkoutAttributesUpdateV2(
            $checkoutId: ID!
            $input: CheckoutAttributesUpdateV2Input!
          ) {
            checkoutAttributesUpdateV2(checkoutId: $checkoutId, input: $input) {
              checkout {
                customAttributes {
                  key
                  value
                }
                note
              }
              checkoutUserErrors {
                code
                field
                message
              }
            }
          }
        `,
        variables: {
          checkoutId: checkoutId,
          input,
        },
      }),
    }),
    getProductsByIDs: builder.query({
      query: (ids) => ({
        body: gql`
          query getProductsByIDs($ids: [ID!]!) {
            nodes(ids: $ids) {
              ... on Product {
                id
                title
                descriptionHtml
                images(first: 1) {
                  edges {
                    node {
                      altText
                      url(transform: { maxHeight: 250 })
                    }
                  }
                }
                variants(first: 50) {
                  edges {
                    node {
                      id
                      price {
                        amount
                        currencyCode
                      }
                      quantityAvailable
                    }
                  }
                }
              }
            }
          }
        `,
        variables: {
          ids,
        },
      }),
    }),
    getVolumeDiscountProductsByIDs: builder.query({
      query: (ids) => ({
        body: gql`
          query getVolumeDiscountProductsByIDs($ids: [ID!]!) {
            nodes(ids: $ids) {
              ... on Product {
                id
                variants(first: 50) {
                  edges {
                    node {
                      id
                    }
                  }
                }
              }
            }
          }
        `,
        variables: {
          ids,
        },
      }),
    }),
    getCollectionByHandle: builder.mutation({
      query: ({ handle, after }) => ({
        body: gql`
          query getCollectionByHandle($handle: String!, $after: String) {
            collectionByHandle(handle: $handle) {
              id
              products(first: 20, after: $after) {
                pageInfo {
                  hasNextPage
                }
                edges {
                  cursor
                  node {
                    id
                    title
                    descriptionHtml
                    images(first: 1) {
                      edges {
                        node {
                          altText
                          url(transform: { maxHeight: 250 })
                        }
                      }
                    }
                    variants(first: 1) {
                      edges {
                        node {
                          id
                          price {
                            amount
                            currencyCode
                          }
                          quantityAvailable
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        `,
        variables: {
          handle,
          after,
        },
      }),
    }),
    getProductsByQuery: builder.mutation({
      query: ({ query, after }) => ({
        body: gql`
          query getProductsByQuery($query: String!, $after: String) {
            products(query: $query, first: 20, after: $after) {
              pageInfo {
                hasNextPage
              }
              edges {
                cursor
                node {
                  id
                  title
                  descriptionHtml
                  images(first: 1) {
                    edges {
                      node {
                        altText
                        url(transform: { maxHeight: 250 })
                      }
                    }
                  }
                  variants(first: 1) {
                    edges {
                      node {
                        id
                        price {
                          amount
                          currencyCode
                        }
                        quantityAvailable
                      }
                    }
                  }
                }
              }
            }
          }
        `,
        variables: {
          query,
          after,
        },
      }),
    }),
    getShopPolicies: builder.query({
      query: () => ({
        body: gql`
          {
            shop {
              privacyPolicy {
                body
              }
              refundPolicy {
                body
              }
              shippingPolicy {
                body
              }
              termsOfService {
                body
              }
              subscriptionPolicy {
                body
              }
            }
          }
        `,
      }),
    }),
  }),
})

export const {
  useLazyGetCustomerQuery,
  useLazyGetCheckoutQuery,
  useGetCheckoutReadyMutation,
  useCreateCheckoutMutation,
  useReplaceLineItemsMutation,
  useAddLineItemsMutation,
  useRemoveLineItemsMutation,
  useUpdateEmailAddressMutation,
  useUpdateShippingAddressMutation,
  useGetAvailableShippingRatesMutation,
  useUpdateShippingLineMutation,
  useApplyDiscountCodeMutation,
  useAutoApplyDiscountCodeMutation,
  useRemoveDiscountCodeMutation,
  useApplyGiftCardMutation,
  useRemoveGiftCardMutation,
  useUpdateCheckoutAttributesMutation,
  useLazyGetProductsByIDsQuery,
  useLazyGetVolumeDiscountProductsByIDsQuery,
  useGetCollectionByHandleMutation,
  useGetProductsByQueryMutation,
  useLazyGetShopPoliciesQuery,
} = storefrontApi

export default storefrontApi
